import styled, { useTheme } from 'styled-components'

import Slider from 'components/Slider/Slider'
import { Typography } from 'components/Typography/Typography'
import { useIsMobile } from 'utils/useIsMobile'
import { Spinner } from 'components/Spinner/Spinner'

const SectionLabel = styled(Typography)`
  display: flex;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 16px;
  svg {
    display: flex;
    cursor: pointer;
    margin-left: 6px;
    path {
      stroke: ${p => p.theme.colors.text.secondary};
    }
  }
`

const AmountSelectorContainer = styled.div`
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.border};
  padding: 12px 10px;
  margin-bottom: 24px;
`

const AmountPercentageContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    flex-direction: column;
  }
`

const PercentageContainer = styled.div<{ $isDisabled: boolean }>`
  display: flex;
  gap: 8px;
  & > div {
    border-radius: 8px;
    background: ${p => p.theme.colors.button.lightBackground};
    display: flex;
    padding: 10px;
    justify-content: center;
    align-items: center;
    cursor: ${p => (p.$isDisabled ? 'auto' : 'pointer')};
    opacity: ${p => (p.$isDisabled ? 0.5 : 1)};
    ${p => p.$isDisabled && `pointer-events:none;`};
    & > div {
      color: ${p => p.theme.colors.button.lightBackgroundText};
    }
  }
`

interface Props {
  isPositionInActive: boolean
  amountPercentage: number
  handleAmountPercentageChange: (newValue: number) => void
  isFrozenPosition: boolean
  isLoading: boolean
}

export const MIN_SLIDER = 0
export const MAX_SLIDER = 100
const STEP_SLIDER = 1

export const AmountSlider = ({
  isPositionInActive,
  amountPercentage,
  handleAmountPercentageChange,
  isFrozenPosition,
  isLoading,
}: Props) => {
  const isMobile = useIsMobile()
  const theme = useTheme()

  return isPositionInActive ? null : (
    <>
      <SectionLabel variant="body" color="secondary" style={{ position: 'relative' }}>
        Amount
        {isLoading && (
          <div style={{ position: 'absolute', top: 0, left: 80 }}>
            <Spinner style={{ width: 24, height: 24 }} color={theme.colors.text.primary} />
          </div>
        )}
      </SectionLabel>
      <AmountSelectorContainer>
        <AmountPercentageContainer>
          <Typography
            variant={isMobile ? 'caption' : 'description'}
            fontWeight="bold"
            style={isMobile ? { marginBottom: 8 } : {}}
          >{`${amountPercentage}%`}</Typography>
          <PercentageContainer $isDisabled={isFrozenPosition}>
            <div onClick={() => handleAmountPercentageChange(25)}>
              <Typography variant="caption">25%</Typography>
            </div>
            <div onClick={() => handleAmountPercentageChange(50)}>
              <Typography variant="caption">50%</Typography>
            </div>
            <div onClick={() => handleAmountPercentageChange(75)}>
              <Typography variant="caption">75%</Typography>
            </div>
            <div onClick={() => handleAmountPercentageChange(100)}>
              <Typography variant="caption">MAX</Typography>
            </div>
          </PercentageContainer>
        </AmountPercentageContainer>
        <Slider
          hideLabels
          min={MIN_SLIDER}
          max={MAX_SLIDER}
          step={STEP_SLIDER}
          onChange={handleAmountPercentageChange}
          value={amountPercentage}
          disabled={isFrozenPosition}
        />
      </AmountSelectorContainer>
    </>
  )
}
